// Farming unit editor — "Meta data" tab: the farming unit's basic info and,
// behind WT_PROTOCOL, its water treatment — what production's farming unit
// modal edits (BasicInfoComponent + WaterTreatmentComponent). Changes land in
// the editor's farming unit draft through `onChange(path, value)`, the
// modal's updateFarmingUnitProperties, and "Save changes" sends them.
const React = require('react');
const { useSelector } = require('react-redux');
const { useTranslation } = require('react-i18next');
const WaterTreatmentComponent = require('WaterTreatmentComponent');
const {
  get_configuration_config,
  FeatureFlagDictionary,
} = require('remoteConfigurationsManager');
const { COUNTRY_REGIONS } = require('./newSeasonConstants');

const FormRow = ({ label, children }) => (
  <div className='ns-form-row'>
    <label className='ns-form-label'>{label}</label>
    <div className='ns-form-input'>{children}</div>
  </div>
);

const SelectInput = ({ value, options, onChange, placeholder, disabled }) => (
  <select
    className='form-control custom-select ns-input'
    value={value ?? ''}
    disabled={disabled}
    onChange={(event) => onChange(event.target.value)}
  >
    <option value='' disabled>
      {placeholder}
    </option>
    {options.map((option) => (
      <option key={option} value={option}>
        {option}
      </option>
    ))}
  </select>
);

// FarmingUnitModal reads WT_PROTOCOL for the unit and its distribution.
const useWaterTreatmentFlag = (farmingUnitName, distributionName) => {
  const configurationData = useSelector((state) => state.configuration_data);
  return Boolean(
    get_configuration_config(
      configurationData,
      FeatureFlagDictionary.WT_PROTOCOL,
      farmingUnitName,
      distributionName,
    ),
  );
};

const BasicInfo = ({ farmingUnit, readOnly, onChange }) => {
  const { t } = useTranslation();
  // As BasicInfoComponent: choosing a country leaves the region as it is.
  const regions = COUNTRY_REGIONS[farmingUnit.country] || [];
  return (
    <>
      <FormRow label={t('new_season_unit_name')}>
        <input
          className='form-control ns-input'
          value={farmingUnit.name}
          placeholder={t('new_season_enter')}
          disabled={readOnly}
          onChange={(event) => onChange('name', event.target.value)}
        />
      </FormRow>
      <FormRow label={t('new_season_monday_id')}>
        <input
          className='form-control ns-input'
          value={farmingUnit.mondayID}
          placeholder={t('new_season_enter')}
          disabled={readOnly}
          onChange={(event) => onChange('mondayID', event.target.value)}
        />
      </FormRow>
      <FormRow label={t('country')}>
        <SelectInput
          value={farmingUnit.country}
          options={Object.keys(COUNTRY_REGIONS)}
          placeholder={t('new_season_select')}
          disabled={readOnly}
          onChange={(value) => onChange('country', value)}
        />
      </FormRow>
      <FormRow label={t('region')}>
        <SelectInput
          value={farmingUnit.region}
          options={regions}
          placeholder={t('new_season_select')}
          disabled={readOnly || !farmingUnit.country}
          onChange={(value) => onChange('region', value)}
        />
      </FormRow>
    </>
  );
};

const FarmingUnitMetaTab = ({
  farmingUnit,
  readOnly,
  onChange,
  distributionName,
}) => {
  const { t } = useTranslation();
  const showWaterTreatment = useWaterTreatmentFlag(
    farmingUnit.name,
    distributionName,
  );
  return (
    <div className='ns-fu-meta'>
      <BasicInfo
        farmingUnit={farmingUnit}
        readOnly={readOnly}
        onChange={onChange}
      />
      {showWaterTreatment && (
        <div className='ns-fu-meta-section'>
          <div className='ns-section-title'>{t('irrigation_system')}</div>
          <WaterTreatmentComponent
            source='farming_unit_editor'
            currentFarmingUnit={farmingUnit}
            onChangeFarmingUnitProperties={onChange}
            shouldDisplayAsDisabled={readOnly}
          />
        </div>
      )}
    </div>
  );
};

module.exports = FarmingUnitMetaTab;
